// Header that folds a details block away: the whole row is the toggle, not just its arrow.

import { ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Panels turn a chevron; cards inside them use the two-way expand arrows. Both states are the
 * same glyph transformed, so the icon turns instead of snapping to a different one.
 */
export function CollapseHeader({
  open,
  label,
  variant = 'expand',
  onToggle,
  className,
  children,
}: {
  open: boolean;
  label: string;
  variant?: 'chevron' | 'expand';
  onToggle: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  const Icon = variant === 'chevron' ? ChevronDown : ChevronsUpDown;
  const turned = variant === 'chevron' ? !open && '-rotate-90' : open && '-scale-y-100';

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-2 rounded-md text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        className,
      )}
    >
      {children}
      <Icon
        aria-hidden
        className={cn('size-[18px] shrink-0 text-gray-900 transition-transform duration-200 motion-reduce:transition-none', turned)}
      />
    </button>
  );
}

/** Animates the height both ways through a grid row; the content stays mounted, `inert` while closed. */
export function CollapseBody({
  open,
  className,
  rootClassName,
  children,
}: {
  open: boolean;
  className?: string;
  /** On the animated wrapper itself, e.g. `min-h-0 flex-1` so the body can shrink inside a capped card. */
  rootClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      inert={!open}
      className={cn(
        'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none',
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        rootClassName,
      )}
    >
      <div className={cn('min-h-0 overflow-hidden', className)}>{children}</div>
    </div>
  );
}
